import { useLiveQuery } from "dexie-react-hooks";
import { Archive, ArchiveRestore, Bell, Check, ChevronLeft, ChevronRight, Flame, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { colorHex, toISODate, type Habit } from "@todoer/shared";
import { toggleHabit } from "../components/Habits.js";
import { confirmDialog, Dialog, Menu, Select } from "../components/overlay.js";
import { SwipeRow } from "../components/Swipe.js";
import { Button, ColorPicker, cx, EmptyState, Field, IconButton, Input, PageHeader, SegmentedControl, useIsPhone } from "../components/ui.js";
import { db, useUser } from "../lib/db.js";
import { addDays, habitProgress, weekStartOf, type HabitProgress } from "../lib/habits.js";
import { cmd, dispatch, uuid } from "../lib/sync.js";

const ICONS = ["✅", "💧", "🏃", "📚", "🧘", "🥗", "😴", "💪", "🎸", "✍️", "🧹", "💊", "🌱", "📵", "🚶", "🙏"];
const dayLabel = (d: string) => new Date(`${d}T00:00`).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" });
const target = (h: Habit) => (h.targetPerWeek >= 7 ? "Every day" : `${h.targetPerWeek}× a week`);

function Streak({ p }: { p: HabitProgress }) {
  return (
    <span className={cx("tabular inline-flex items-center gap-1 text-body font-semibold", p.streak > 0 ? "text-p2" : "text-text-faint")}
      data-tip={`Best: ${p.best} ${p.unit}${p.best === 1 ? "" : "s"} · Total: ${p.total}`}>
      <Flame size={16} aria-hidden fill={p.streak > 0 ? "currentColor" : "none"} />
      {p.streak > 0 ? `${p.streak}${p.unit === "week" ? "w" : "d"}` : "–"}
      <span className="sr-only">{p.streak > 0 ? ` streak` : "No streak"}</span>
    </span>
  );
}

function DayCircle({ h, d, done, today, size = "md" }: { h: Habit; d: string; done: boolean; today: string; size?: "md" | "lg" }) {
  const color = colorHex(h.color);
  return (
    <button type="button" disabled={d > today} onClick={() => void toggleHabit(h, d, done)} aria-pressed={done}
      aria-label={`${h.name} on ${dayLabel(d)}: ${done ? "done" : "not done"}`}
      className={cx("grid shrink-0 place-items-center rounded-full border-[1.5px] text-white transition-[background-color,border-color,transform] duration-200 active:scale-90 disabled:opacity-30",
        size === "lg" ? "h-11 w-11" : "h-8 w-8", done ? "" : "border-border-strong hover:border-text-faint", d === today && !done && "border-text-muted")}
      style={done ? { background: color, borderColor: color } : undefined}>
      {done && <Check size={size === "lg" ? 18 : 16} strokeWidth={3} aria-hidden className="anim-check" />}
    </button>
  );
}

export function Habits() {
  const user = useUser();
  const sow = user?.startOfWeek ?? 1;
  const today = toISODate(new Date());
  const isPhone = useIsPhone();
  const [offset, setOffset] = useState(0);
  const [edit, setEdit] = useState<Habit | "new" | null>(null);
  const [show, setShow] = useState<"active" | "archived">("active");
  const start = addDays(weekStartOf(today, sow), offset * 7);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const data = useLiveQuery(async () => {
    const habits = (await db.habits.toArray()).sort((a, b) => a.itemOrder - b.itemOrder);
    const checkins = await db.habitCheckins.toArray();
    const byHabit = new Map<string, Set<string>>();
    for (const c of checkins) byHabit.set(c.habitId, (byHabit.get(c.habitId) ?? new Set()).add(c.date));
    return { habits, byHabit };
  }, []);
  const habits = (data?.habits ?? []).filter((h) => h.isArchived === (show === "archived"));
  const range = `${new Date(`${days[0]}T00:00`).toLocaleDateString([], { day: "numeric", month: "short" })} – ${new Date(`${days[6]}T00:00`).toLocaleDateString([], { day: "numeric", month: "short" })}`;

  const archive = (h: Habit) => void dispatch(cmd("habit_update", { id: h.id, isArchived: !h.isArchived }));
  const remove = async (h: Habit) => { if (await confirmDialog({ title: `Delete “${h.name}”?`, body: "Its history goes too. Archive it instead to keep the history.", confirmLabel: "Delete", danger: true })) void dispatch(cmd("habit_delete", { id: h.id })); };
  const menu = (h: Habit) => (
    <Menu size="sm" label={`${h.name} actions`} items={[
      { label: "Edit", icon: Pencil, onSelect: () => setEdit(h) },
      { label: h.isArchived ? "Unarchive" : "Archive", icon: h.isArchived ? ArchiveRestore : Archive, onSelect: () => archive(h) },
      { label: "Delete", icon: Trash2, danger: true, divider: true, onSelect: () => void remove(h) },
    ]} />
  );

  return (
    <div>
      <PageHeader title="Habits" subtitle="Build routines with streaks and optional daily reminders."
        actions={<Button variant="primary" icon={Plus} onClick={() => setEdit("new")} aria-label="New habit"><span className="max-sm:hidden">New habit</span></Button>} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <IconButton icon={ChevronLeft} label="Previous week" onClick={() => setOffset((o) => o - 1)} />
          <span className="tabular min-w-28 text-center text-body font-medium">{range}</span>
          <IconButton icon={ChevronRight} label="Next week" disabled={offset >= 0} onClick={() => setOffset((o) => o + 1)} />
          {offset !== 0 && <Button size="sm" variant="ghost" onClick={() => setOffset(0)}>This week</Button>}
        </div>
        <SegmentedControl label="Show" size="sm" className="ml-auto" value={show} onChange={setShow} options={[{ value: "active", label: "Active" }, { value: "archived", label: "Archived" }]} />
      </div>

      {!habits.length ? (
        <EmptyState kind="habits" title={show === "archived" ? "No archived habits" : "No habits yet"}
          action={show === "active" && <Button variant="primary" icon={Plus} onClick={() => setEdit("new")}>New habit</Button>}>
          Track things like drinking water, reading or exercise. Tick them off here or from Today.
        </EmptyState>
      ) : isPhone ? (
        <ul className="space-y-3">
          {habits.map((h) => {
            const dates = data?.byHabit.get(h.id) ?? new Set<string>();
            const p = habitProgress(dates, h.targetPerWeek, today, sow);
            return (
              <li key={h.id}>
                <SwipeRow className="rounded-card" contentClassName="rounded-card border border-border bg-surface-raised"
                  left={[
                    { key: "edit", label: "Edit", icon: Pencil, tone: "info", onSelect: () => setEdit(h) },
                    { key: "archive", label: h.isArchived ? "Restore" : "Archive", icon: Archive, tone: "warning", onSelect: () => archive(h) },
                    { key: "delete", label: "Delete", icon: Trash2, tone: "danger", onSelect: () => void remove(h) },
                  ]}>
                  <div className="p-3">
                    <div className="flex items-center gap-3">
                      <span aria-hidden className="grid h-10 w-10 shrink-0 place-items-center rounded-control text-lead" style={{ background: `color-mix(in srgb, ${colorHex(h.color)} 14%, transparent)` }}>{h.icon}</span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-body font-semibold">{h.name}</div>
                        <div className="text-meta text-text-faint">{target(h)} · {p.thisWeek}/{Math.min(7, h.targetPerWeek)} this week</div>
                      </div>
                      <Streak p={p} />
                    </div>
                    <div className="mt-3 flex justify-between">
                      {days.map((d) => (
                        <div key={d} className="flex flex-col items-center gap-1">
                          <span className={cx("text-caption font-medium", d === today ? "text-brand-text" : "text-text-faint")} aria-hidden>{new Date(`${d}T00:00`).toLocaleDateString([], { weekday: "narrow" })}</span>
                          <DayCircle h={h} d={d} done={dates.has(d)} today={today} size="lg" />
                        </div>
                      ))}
                    </div>
                  </div>
                </SwipeRow>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="overflow-hidden rounded-card border border-border bg-surface-raised">
          <table className="w-full text-body">
            <thead>
              <tr className="border-b border-border bg-surface-sunken">
                <th scope="col" className="px-4 py-2 text-left text-caption font-semibold uppercase text-text-faint">Habit</th>
                {days.map((d) => (
                  <th key={d} scope="col" className={cx("w-12 py-2 text-center text-caption font-semibold uppercase", d === today ? "text-brand-text" : "text-text-faint")}>
                    {new Date(`${d}T00:00`).toLocaleDateString([], { weekday: "short" })}
                    <div className="tabular text-meta normal-case tracking-normal">{Number(d.slice(8))}</div>
                  </th>
                ))}
                <th scope="col" className="w-20 pr-2 text-right text-caption font-semibold uppercase text-text-faint">Streak</th>
                <th className="w-12"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {habits.map((h) => {
                const dates = data?.byHabit.get(h.id) ?? new Set<string>();
                const p = habitProgress(dates, h.targetPerWeek, today, sow);
                return (
                  <tr key={h.id} className="group border-b border-border last:border-0 hover:bg-hover">
                    <th scope="row" className="px-4 py-3 text-left font-normal">
                      <div className="flex items-center gap-3">
                        <span aria-hidden className="grid h-9 w-9 shrink-0 place-items-center rounded-control text-lead" style={{ background: `color-mix(in srgb, ${colorHex(h.color)} 14%, transparent)` }}>{h.icon}</span>
                        <div className="min-w-0">
                          <div className="truncate font-semibold">{h.name}</div>
                          <div className="flex items-center gap-1 text-meta text-text-faint">
                            {target(h)} · {p.thisWeek}/{Math.min(7, h.targetPerWeek)} this week
                            {h.reminderTime && <><span aria-hidden>·</span><Bell size={12} aria-label="Reminder" />{h.reminderTime}</>}
                          </div>
                        </div>
                      </div>
                    </th>
                    {days.map((d) => <td key={d} className="text-center"><div className="flex justify-center"><DayCircle h={h} d={d} done={dates.has(d)} today={today} /></div></td>)}
                    <td className="pr-2 text-right"><Streak p={p} /></td>
                    <td className="pr-2 text-right">{menu(h)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {edit && <HabitDialog habit={edit === "new" ? undefined : edit} count={data?.habits.length ?? 0} onClose={() => setEdit(null)} />}
    </div>
  );
}

function HabitDialog({ habit, count, onClose }: { habit?: Habit; count: number; onClose: () => void }) {
  const [name, setName] = useState(habit?.name ?? "");
  const [icon, setIcon] = useState(habit?.icon ?? "✅");
  const [color, setColor] = useState(habit?.color ?? "green");
  const [targetN, setTarget] = useState(habit?.targetPerWeek ?? 7);
  const [reminder, setReminder] = useState(habit?.reminderTime ?? "");
  const save = async () => {
    if (!name.trim()) return;
    const args = { name: name.trim(), icon, color, targetPerWeek: targetN, reminderTime: reminder || null };
    if (habit) await dispatch(cmd("habit_update", { id: habit.id, ...args }));
    else await dispatch(cmd("habit_add", { id: uuid(), ...args, itemOrder: count + 1 }));
    onClose();
  };
  return (
    <Dialog title={habit ? "Edit habit" : "New habit"} onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!name.trim()} onClick={() => void save()}>Save</Button></>}>
      <div className="space-y-5">
        <Field label="Name" htmlFor="hd-name"><Input id="hd-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Drink 2 litres of water" /></Field>
        <div>
          <span className="mb-1.5 block text-meta font-medium text-text-muted">Icon</span>
          <div className="grid grid-cols-8 gap-1.5" role="radiogroup" aria-label="Icon">
            {ICONS.map((i) => (
              <button key={i} type="button" role="radio" aria-checked={icon === i} aria-label={`Icon ${i}`} onClick={() => setIcon(i)}
                className={cx("grid aspect-square place-items-center rounded-control text-lead transition-colors phone:min-h-10", icon === i ? "bg-brand-soft ring-2 ring-brand" : "hover:bg-hover")}>{i}</button>
            ))}
          </div>
        </div>
        <div><span className="mb-1.5 block text-meta font-medium text-text-muted">Colour</span><ColorPicker value={color} onChange={setColor} /></div>
        <div>
          <span className="mb-1.5 block text-meta font-medium text-text-muted">How often</span>
          <Select label="How often" value={targetN} onChange={setTarget} options={[{ value: 7, label: "Every day" }, ...[6, 5, 4, 3, 2, 1].map((n) => ({ value: n, label: `${n}× a week` }))]} />
        </div>
        <Field label="Daily reminder (optional)" htmlFor="hd-rem" hint="Sent as a push notification if you haven't ticked it off. Turn on notifications in Settings.">
          <Input id="hd-rem" type="time" value={reminder} onChange={(e) => setReminder(e.target.value)} className="tabular w-40" />
        </Field>
      </div>
    </Dialog>
  );
}
